import { useEffect, useId, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
  useReducedMotion,
  animate,
} from 'framer-motion';

/**
 * Reveal-Lens Hero — visual embodiment of:
 *   "See what others miss. Decide what others can't."
 *
 * A calm Ink map with property pins. A saffron lens orbits a lemniscate
 * (figure-8) path; wherever it passes, the hidden insight layer underneath
 * is revealed — flood zones, school catchments, green cover, future sparks.
 * Outside the lens, the map looks ordinary. That's the point.
 */

const VB = 480;

const PINS = [
  { x: 120, y: 150 },
  { x: 220, y: 110 },
  { x: 340, y: 170 },
  { x: 160, y: 270 },
  { x: 290, y: 260 },
  { x: 380, y: 320 },
  { x: 220, y: 360 },
  { x: 110, y: 360 },
];

const SCHOOL_ZONE =
  'M 70,90 C 130,60 230,70 280,120 C 320,160 290,220 230,235 C 170,250 90,220 70,170 Z';
const FLOOD_BLOB =
  'M 220,300 C 290,280 380,300 400,360 C 410,400 360,440 290,430 C 220,420 180,380 195,340 Z';
const GREEN_PARK =
  'M 60,300 C 110,280 170,290 180,340 C 185,380 130,410 80,395 C 40,385 30,330 60,300 Z';
const FUTURE_SPARKS = [
  { x: 360, y: 110 },
  { x: 90, y: 200 },
  { x: 300, y: 380 },
];

// Building footprints — eagle-eye view. Clustered in plots between roads,
// varied sizes & slight rotations so they read as a real urban grid.
const BUILDINGS = [
  // Cluster A — top-left, small residential
  { x: 30, y: 60, w: 18, h: 26, r: -8 },
  { x: 55, y: 55, w: 22, h: 16, r: -8 },
  { x: 85, y: 68, w: 16, h: 24, r: -6 },
  { x: 30, y: 100, w: 28, h: 14, r: -8 },
  { x: 65, y: 95, w: 14, h: 22, r: -6 },
  { x: 92, y: 105, w: 22, h: 16, r: -4 },
  { x: 35, y: 135, w: 18, h: 22, r: -10 },
  { x: 60, y: 130, w: 26, h: 14, r: -8 },
  { x: 95, y: 138, w: 18, h: 20, r: -6 },
  // Cluster B — top-right, commercial (bigger plates)
  { x: 290, y: 48, w: 32, h: 22, r: 12 },
  { x: 335, y: 62, w: 28, h: 28, r: 12 },
  { x: 290, y: 88, w: 24, h: 16, r: 10 },
  { x: 325, y: 108, w: 30, h: 20, r: 10 },
  { x: 380, y: 50, w: 22, h: 24, r: 14 },
  { x: 380, y: 88, w: 28, h: 16, r: 14 },
  { x: 410, y: 130, w: 24, h: 22, r: 12 },
  // Cluster C — central business
  { x: 170, y: 175, w: 20, h: 24, r: 5 },
  { x: 198, y: 195, w: 26, h: 16, r: 5 },
  { x: 170, y: 220, w: 22, h: 18, r: 5 },
  { x: 240, y: 175, w: 18, h: 22, r: -3 },
  { x: 268, y: 198, w: 24, h: 18, r: -3 },
  { x: 305, y: 175, w: 16, h: 26, r: 0 },
  // Cluster D — bottom-left residential
  { x: 60, y: 318, w: 22, h: 16, r: -5 },
  { x: 90, y: 305, w: 18, h: 22, r: -7 },
  { x: 112, y: 338, w: 26, h: 14, r: -5 },
  { x: 60, y: 358, w: 18, h: 22, r: -8 },
  { x: 90, y: 378, w: 24, h: 16, r: -6 },
  { x: 122, y: 372, w: 18, h: 20, r: -4 },
  // Cluster E — bottom-right
  { x: 280, y: 305, w: 26, h: 20, r: 8 },
  { x: 320, y: 322, w: 18, h: 26, r: 8 },
  { x: 348, y: 350, w: 22, h: 16, r: 6 },
  { x: 288, y: 362, w: 26, h: 14, r: 8 },
  { x: 320, y: 382, w: 16, h: 22, r: 10 },
  { x: 358, y: 408, w: 22, h: 16, r: 8 },
  // Sparser scatter
  { x: 200, y: 60, w: 16, h: 20, r: 3 },
  { x: 232, y: 78, w: 22, h: 14, r: 3 },
  { x: 130, y: 200, w: 18, h: 20, r: -2 },
  { x: 380, y: 232, w: 22, h: 16, r: 14 },
  { x: 200, y: 320, w: 18, h: 22, r: 2 },
  { x: 145, y: 65, w: 14, h: 20, r: -4 },
];

function lemniscate(t, a) {
  const denom = 1 + Math.sin(t) * Math.sin(t);
  const x = (a * Math.cos(t)) / denom;
  const y = (a * Math.sin(t) * Math.cos(t)) / denom;
  return { x, y };
}

export default function HeroAnimation() {
  const prefersReduced = useReducedMotion();
  const containerRef = useRef(null);
  const maskId = useId();
  const glowId = useId();
  const gridId = useId();
  const [size, setSize] = useState(480);

  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      // Use the smaller of viewport width and a comfortable cap so the disc
      // never overflows narrow phones.
      const padding = w < 640 ? 40 : 80;
      const available = w - padding;
      if (w < 380) setSize(Math.min(available, 260));
      else if (w < 640) setSize(Math.min(available, 320));
      else if (w < 1024) setSize(Math.min(available, 420));
      else setSize(480);
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const t = useMotionValue(0);

  useEffect(() => {
    if (prefersReduced) {
      t.set(Math.PI / 4);
      return;
    }
    const controls = animate(t, Math.PI * 2, {
      duration: 14,
      ease: 'linear',
      repeat: Infinity,
    });
    return () => controls.stop();
  }, [t, prefersReduced]);

  const cx = useTransform(t, (v) => VB / 2 + lemniscate(v, VB * 0.32).x);
  const cy = useTransform(t, (v) => VB / 2 + lemniscate(v, VB * 0.32).y);

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotateX = useSpring(useTransform(py, [-1, 1], [9, -9]), {
    stiffness: 120,
    damping: 14,
  });
  const rotateY = useSpring(useTransform(px, [-1, 1], [-9, 9]), {
    stiffness: 120,
    damping: 14,
  });

  function handlePointer(e) {
    if (prefersReduced) return;
    const r = containerRef.current.getBoundingClientRect();
    px.set(((e.clientX - r.left) / r.width) * 2 - 1);
    py.set(((e.clientY - r.top) / r.height) * 2 - 1);
  }
  function handleLeave() {
    px.set(0);
    py.set(0);
  }

  const lensR = VB * 0.18;
  const lensSoftR = VB * 0.3;

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ perspective: 1200 }}
    >
      <motion.div
        ref={containerRef}
        onPointerMove={handlePointer}
        onPointerLeave={handleLeave}
        style={{
          width: size,
          height: size,
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        className="relative"
      >
        <div
          className="absolute inset-0 rounded-full shadow-ink"
          style={{
            background:
              'radial-gradient(circle at 30% 25%, #1a2438 0%, #0B1220 55%, #05080F 100%)',
          }}
        />
        <div className="grain absolute inset-0 overflow-hidden rounded-full" />

        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          className="absolute inset-0 h-full w-full"
        >
          <defs>
            <pattern
              id={gridId}
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
              />
            </pattern>

            <mask id={maskId} maskUnits="userSpaceOnUse">
              <rect width={VB} height={VB} fill="black" />
              <motion.circle cx={cx} cy={cy} r={lensR} fill="white" />
              <motion.circle
                cx={cx}
                cy={cy}
                r={lensSoftR}
                fill={`url(#${glowId})`}
              />
            </mask>

            <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="55%" stopColor="white" stopOpacity="0.5" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>

            <clipPath id={`clip-${maskId}`}>
              <circle cx={VB / 2} cy={VB / 2} r={VB / 2} />
            </clipPath>
          </defs>

          <g clipPath={`url(#clip-${maskId})`}>
            {/* === BASELINE LAYER === */}
            <rect width={VB} height={VB} fill={`url(#${gridId})`} />

            {/* Coastline / river — soft cool sweep on the right edge */}
            <path
              d="M 480,40 C 430,120 460,200 420,260 C 380,320 430,380 410,460 L 480,480 L 480,0 Z"
              fill="rgba(59,130,246,0.05)"
              stroke="rgba(96,165,250,0.18)"
              strokeWidth="1"
            />

            {/* Primary arterials */}
            <path
              d="M -10,275 C 90,250 180,300 250,260 C 320,220 410,260 490,235"
              fill="none"
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="2.25"
              strokeLinecap="round"
            />
            <path
              d="M 215,-10 C 235,90 195,170 230,250 C 260,320 220,400 245,490"
              fill="none"
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="2.25"
              strokeLinecap="round"
            />

            {/* Secondary roads */}
            <path
              d="M -10,140 C 80,150 160,120 240,150 C 320,180 400,150 490,170"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1.25"
            />
            <path
              d="M -10,395 C 90,380 170,410 250,390 C 330,370 410,400 490,385"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1.25"
            />
            <path
              d="M 90,-10 C 110,80 80,160 130,240 C 175,310 140,400 165,490"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1.25"
            />
            <path
              d="M 360,-10 C 345,80 380,160 350,240 C 320,310 360,400 340,490"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1.25"
            />

            {/* Diagonal connectors / ring road */}
            <path
              d="M 60,60 C 180,120 300,90 420,180"
              fill="none"
              stroke="rgba(255,255,255,0.07)"
              strokeWidth="1"
              strokeDasharray="3 4"
            />
            <path
              d="M 60,420 C 180,360 300,400 420,330"
              fill="none"
              stroke="rgba(255,255,255,0.07)"
              strokeWidth="1"
              strokeDasharray="3 4"
            />

            {/* Tertiary streets — local lanes weaving through clusters */}
            <g
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="0.75"
              strokeLinecap="round"
            >
              {/* Top-left cluster lanes */}
              <path d="M 25,80 L 130,80" />
              <path d="M 25,118 L 130,118" />
              <path d="M 50,50 L 50,170" />
              <path d="M 80,50 L 80,170" />
              <path d="M 110,50 L 110,170" />

              {/* Top-right cluster lanes */}
              <path d="M 280,40 L 440,40" />
              <path d="M 280,80 L 440,80" />
              <path d="M 280,125 L 440,125" />
              <path d="M 310,30 L 310,160" />
              <path d="M 360,30 L 360,160" />
              <path d="M 410,30 L 410,160" />

              {/* Central business — denser grid */}
              <path d="M 160,165 L 330,165" />
              <path d="M 160,210 L 330,210" />
              <path d="M 160,245 L 330,245" />
              <path d="M 190,160 L 190,260" />
              <path d="M 230,160 L 230,260" />
              <path d="M 270,160 L 270,260" />
              <path d="M 310,160 L 310,260" />

              {/* Bottom-left lanes */}
              <path d="M 50,300 L 160,300" />
              <path d="M 50,340 L 160,340" />
              <path d="M 50,400 L 160,400" />
              <path d="M 75,290 L 75,420" />
              <path d="M 110,290 L 110,420" />
              <path d="M 145,290 L 145,420" />

              {/* Bottom-right lanes — slight curve to hint coast */}
              <path d="M 270,300 Q 340,295 410,310" />
              <path d="M 270,345 Q 340,340 405,355" />
              <path d="M 275,395 Q 345,388 395,405" />
              <path d="M 300,295 L 300,425" />
              <path d="M 340,295 L 340,425" />
              <path d="M 380,300 L 380,425" />

              {/* A couple of curving shortcuts */}
              <path d="M 130,170 Q 170,200 200,250" />
              <path d="M 270,250 Q 300,290 320,320" />
              <path d="M 200,260 Q 200,300 200,320" />
            </g>

            {/* Junction dots */}
            {[
              [230, 250],
              [130, 240],
              [350, 240],
              [245, 145],
              [240, 390],
            ].map(([x, y], i) => (
              <circle
                key={`j-${i}`}
                cx={x}
                cy={y}
                r="1.6"
                fill="rgba(255,255,255,0.18)"
              />
            ))}

            {/* Building footprints — baseline (faint, beneath pins) */}
            {BUILDINGS.map((b, i) => (
              <rect
                key={`b-${i}`}
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                fill="rgba(255,255,255,0.07)"
                stroke="rgba(255,255,255,0.13)"
                strokeWidth="0.5"
                rx="1"
                transform={`rotate(${b.r} ${b.x + b.w / 2} ${b.y + b.h / 2})`}
              />
            ))}

            {/* Property pins */}
            {PINS.map((p, i) => (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="3"
                  fill="rgba(255,255,255,0.55)"
                />
              </g>
            ))}

            {/* === HIDDEN INSIGHT LAYER (revealed only inside the lens) === */}
            <g mask={`url(#${maskId})`}>
              <path
                d={SCHOOL_ZONE}
                fill="rgba(244,180,0,0.18)"
                stroke="rgba(244,180,0,0.55)"
                strokeWidth="1"
                strokeDasharray="4 3"
              />
              <path
                d={GREEN_PARK}
                fill="rgba(34,197,94,0.16)"
                stroke="rgba(34,197,94,0.5)"
                strokeWidth="1"
                strokeDasharray="4 3"
              />
              <path
                d={FLOOD_BLOB}
                fill="rgba(59,130,246,0.18)"
                stroke="rgba(96,165,250,0.6)"
                strokeWidth="1"
                strokeDasharray="2 4"
              />
              <path
                d="M 250,330 C 300,320 360,335 370,365 C 375,385 340,405 295,400 C 250,395 235,365 250,330 Z"
                fill="none"
                stroke="rgba(96,165,250,0.45)"
                strokeWidth="1"
              />
              <path
                d="M 270,350 C 300,343 340,355 345,375 C 348,388 325,398 300,395 C 275,392 265,375 270,350 Z"
                fill="none"
                stroke="rgba(96,165,250,0.35)"
                strokeWidth="1"
              />

              {/* Buildings lit up by the lens — saffron rooftops */}
              {BUILDINGS.map((b, i) => (
                <rect
                  key={`b-hi-${i}`}
                  x={b.x}
                  y={b.y}
                  width={b.w}
                  height={b.h}
                  fill="rgba(244,180,0,0.32)"
                  stroke="rgba(244,180,0,0.75)"
                  strokeWidth="0.6"
                  rx="1"
                  transform={`rotate(${b.r} ${b.x + b.w / 2} ${b.y + b.h / 2})`}
                />
              ))}

              {PINS.map((p, i) => (
                <circle
                  key={`hi-${i}`}
                  cx={p.x}
                  cy={p.y}
                  r="4"
                  fill="#F4B400"
                />
              ))}

              {FUTURE_SPARKS.map((s, i) => (
                <g key={`s-${i}`} transform={`translate(${s.x} ${s.y})`}>
                  <motion.g
                    animate={
                      prefersReduced
                        ? {}
                        : { scale: [0.9, 1.2, 0.9], opacity: [0.6, 1, 0.6] }
                    }
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      delay: i * 0.4,
                      ease: 'easeInOut',
                    }}
                    style={{ transformOrigin: 'center' }}
                  >
                    <path
                      d="M 0 -8 L 1.6 -1.6 L 8 0 L 1.6 1.6 L 0 8 L -1.6 1.6 L -8 0 L -1.6 -1.6 Z"
                      fill="#FDE68A"
                    />
                  </motion.g>
                </g>
              ))}
            </g>

            {/* === LENS RING === */}
            <motion.circle
              cx={cx}
              cy={cy}
              r={lensR}
              fill="none"
              stroke="rgba(244,180,0,0.7)"
              strokeWidth="1.25"
            />
            <motion.circle
              cx={cx}
              cy={cy}
              r={lensSoftR}
              fill="none"
              stroke="rgba(244,180,0,0.18)"
              strokeWidth="1"
              strokeDasharray="3 4"
            />
            <motion.circle cx={cx} cy={cy} r="2" fill="#F4B400" />
          </g>

          <circle
            cx={VB / 2}
            cy={VB / 2}
            r={VB / 2 - 0.5}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        </svg>

        <RevealLabel cx={cx} cy={cy} size={size} prefersReduced={prefersReduced} />
      </motion.div>
    </div>
  );
}

function RevealLabel({ cx, cy, size, prefersReduced }) {
  const labels = ['Schools', 'Flood risk', 'Future infra', 'Green cover'];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (prefersReduced) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % labels.length), 3500);
    return () => clearInterval(id);
  }, [prefersReduced, labels.length]);

  const left = useTransform(cx, (v) => (v / VB) * size);
  const top = useTransform(cy, (v) => (v / VB) * size - 48);

  return (
    <motion.div
      style={{ left, top, x: '-50%' }}
      className="pointer-events-none absolute"
    >
      <motion.div
        key={idx}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.4 }}
        className="rounded-full border border-saffron-gold/30 bg-ink/70 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-saffron-gold backdrop-blur-md"
      >
        {labels[idx]}
      </motion.div>
    </motion.div>
  );
}
